import ThumbDown from "@mui/icons-material/ThumbDown";
import ThumbUp from "@mui/icons-material/ThumbUp";
import { Stack, Tooltip, Typography } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { UserType } from "encamp-prisma/generated/browser";
import { gql } from "generated-graphql";
import {
  GetScoutConversationsForStaffQuery,
  GetScoutConversationsForStaffQueryVariables,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useNavigate } from "react-router-dom";

export const GET_SCOUT_CONVERSATIONS_FOR_STAFF = gql(`
  query GetScoutConversationsForStaff(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    getScoutConversationsForStaff(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      count
      items {
        id
        title
        previewExcerpt
        status
        updatedAt
        createdAt
        tenant {
          id
          name
        }
        user {
          id
          email
          firstName
          lastName
        }
        usage {
          model
          inputTokens
          outputTokens
          thinkingTokens
          cacheReadTokens
          cacheWriteTokens
          estimatedCostUsd
        }
        feedback {
          upvotes
          downvotes
        }
        staffFeedback {
          id
          rating
          feedback
          submittedByEmail
          createdAt
          updatedAt
        }
      }
    }
  }
`);

type ScoutConversationStaffRow =
  GetScoutConversationsForStaffQuery["getScoutConversationsForStaff"]["items"][number];

export function ScoutConversations() {
  useBreadcrumb({ label: "Scout Conversations" });
  const navigate = useNavigate();

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<
        GetScoutConversationsForStaffQuery,
        GetScoutConversationsForStaffQueryVariables,
        ScoutConversationStaffRow
      >
        persistenceKey="staffScoutConversations"
        isURLDriven
        dataQuery={GET_SCOUT_CONVERSATIONS_FOR_STAFF}
        getItems={(data) => data.getScoutConversationsForStaff.items}
        getCount={(data) => data.getScoutConversationsForStaff.count}
        onRowClick={(params) => {
          navigate(`/staff/observe/scout-conversation/${params.row.id}`);
        }}
        initialSortModel={[{ field: "updatedAt", sort: "desc" }]}
        columns={[
          {
            field: "updatedAt",
            headerName: "Updated",
            flex: 0.4,
            type: "dateTime",
            valueGetter(_, row) {
              return row.updatedAt ? new Date(row.updatedAt) : null;
            },
          },
          {
            field: "tenant",
            headerName: "Tenant",
            flex: 0.6,
            sortable: false,
            // multipleTenant renders MultipleTenantPicker in the filter chip
            // popover so staff can pick tenants from a searchable dropdown
            // instead of typing exact names. The picker serializes the
            // selection as `tenantId:uuid1|uuid2`; the backend handler
            // resolves that to `tenantId: { in: [...] }`.
            filterKeyType: "multipleTenant",
            valueGetter(_, row) {
              return row.tenant?.name ?? "(no tenant)";
            },
          },
          {
            field: "user",
            headerName: "User",
            flex: 0.6,
            sortable: false,
            // multipleStaffAnyUser routes the picker through the
            // staffSearchAnyUser query, which has no tenant scope and no
            // user-type restriction. The plain `multipleUser` picker would
            // throw "No current tenant id set in context" here because the
            // tenant `users` resolver requires a current-tenant context that
            // /staff routes don't have. Selected users serialize as pipe-
            // separated emails, resolved server-side via prisma email lookup.
            filterKeyType: "multipleStaffAnyUser",
            valueGetter(_, row) {
              return row.user?.email ?? row.user?.id ?? "(unknown)";
            },
          },
          {
            field: "title",
            headerName: "Title",
            flex: 0.6,
            valueGetter(_, row) {
              return row.title ?? "(untitled)";
            },
          },
          {
            field: "previewExcerpt",
            headerName: "Preview",
            flex: 1,
            sortable: false,
            valueGetter(_, row) {
              return row.previewExcerpt ?? "";
            },
          },
          {
            field: "status",
            headerName: "Status",
            flex: 0.3,
          },
          {
            field: "feedback",
            headerName: "Feedback",
            flex: 0.5,
            sortable: false,
            // Single-select dropdown so staff don't have to know the
            // accepted values. enumPresentationFunction renders title-cased
            // labels; the lowercased option strings are what the backend
            // handler matches on (`feedback:upvoted`/`feedback:downvoted`).
            filterKeyType: "enum",
            enumValues: ["upvoted", "downvoted"],
            enumPresentationFunction: (value) =>
              value === "upvoted" ? "Upvoted" : value === "downvoted" ? "Downvoted" : value,
            valueGetter(_, row) {
              return row.feedback.upvotes - row.feedback.downvotes;
            },
            renderCell: (params) => {
              const upvotes = params.row.feedback.upvotes;
              const downvotes = params.row.feedback.downvotes;
              if (upvotes === 0 && downvotes === 0) {
                return (
                  <Typography variant="body2" color="text.disabled">
                    —
                  </Typography>
                );
              }
              return (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {upvotes > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ThumbUp sx={{ fontSize: 14, color: "success.main" }} />
                      <Typography variant="body2">{upvotes}</Typography>
                    </Stack>
                  ) : null}
                  {downvotes > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ThumbDown sx={{ fontSize: 14, color: "error.main" }} />
                      <Typography variant="body2">{downvotes}</Typography>
                    </Stack>
                  ) : null}
                </Stack>
              );
            },
          },
          {
            field: "staffFeedback",
            headerName: "Staff Feedback",
            flex: 0.8,
            sortable: false,
            filterable: false,
            valueGetter(_, row) {
              return row.staffFeedback?.feedback ?? "";
            },
            renderCell: (params) => {
              const staffFeedback = params.row.staffFeedback;
              const feedbackText = staffFeedback?.feedback?.trim() ?? "";
              if (!staffFeedback || !feedbackText) {
                return (
                  <Typography variant="body2" color="text.disabled">
                    —
                  </Typography>
                );
              }

              return (
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {feedbackText}
                    </Typography>
                  }
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}
                  >
                    {feedbackText}
                  </Typography>
                </Tooltip>
              );
            },
          },
          {
            field: "model",
            headerName: "Model",
            flex: 0.55,
            sortable: false,
            valueGetter(_, row) {
              return row.usage.model ?? "—";
            },
          },
          {
            field: "inputTokens",
            headerName: "Input tokens",
            flex: 0.4,
            type: "number",
            sortable: false,
            valueGetter(_, row) {
              return row.usage.inputTokens;
            },
            valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
          },
          {
            field: "outputTokens",
            headerName: "Output tokens",
            flex: 0.4,
            type: "number",
            sortable: false,
            valueGetter(_, row) {
              return row.usage.outputTokens;
            },
            valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
          },
          {
            field: "thinkingTokens",
            headerName: "Thinking tokens",
            flex: 0.4,
            type: "number",
            sortable: false,
            valueGetter(_, row) {
              return row.usage.thinkingTokens;
            },
            valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
          },
          {
            field: "cacheReadTokens",
            headerName: "Cache read tokens",
            flex: 0.42,
            type: "number",
            sortable: false,
            valueGetter(_, row) {
              return row.usage.cacheReadTokens;
            },
            valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
          },
          {
            field: "cacheWriteTokens",
            headerName: "Cache write tokens",
            flex: 0.42,
            type: "number",
            sortable: false,
            valueGetter(_, row) {
              return row.usage.cacheWriteTokens;
            },
            valueFormatter: (value) => formatTokensCompact(Number(value ?? 0)),
          },
          {
            field: "estimatedCostUsd",
            headerName: "Estimated cost",
            flex: 0.35,
            type: "number",
            sortable: false,
            valueGetter(_, row) {
              return row.usage.estimatedCostUsd;
            },
            valueFormatter: (value) => formatUsdCost(Number(value ?? 0)),
          },
        ]}
        additionalFilterColumns={[
          {
            key: "messages",
            header: "Messages",
            filterKeyType: "text",
          },
          {
            key: "userType",
            header: "User Type",
            filterKeyType: "enum",
            enumValues: Object.values(UserType),
          },
        ]}
        excludeFilterColumns={[
          "previewExcerpt",
          "updatedAt",
          "staffFeedback",
          "model",
          "inputTokens",
          "outputTokens",
          "thinkingTokens",
          "cacheReadTokens",
          "cacheWriteTokens",
          "estimatedCostUsd",
        ]}
      />
    </Stack>
  );
}

// Compact "$0.0023" / "$1.42" formatting — staff observer values typically
// sit in the cents range, so fixed 4 decimals reads well next to integer
// token counts in the table.
function formatUsdCost(value: number): string {
  if (!Number.isFinite(value) || value === 0) {
    return "$0.0000";
  }
  return `$${value.toFixed(4)}`;
}

// Match `formatTokens` in encamp-scout/core/src/telemetry/pricing.ts so the
// staff list and the per-message viewer agree on how to compact "12345" into
// "12.3K". Inlined rather than imported because oak doesn't depend on the
// scout core runtime.
function formatTokensCompact(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(2)}M`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(1)}K`;
  }
  return tokens.toString();
}
